import { useCallback, useId } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { previewRunRemoval, removeRun } from "../../../api/threads/runRemovalApi";
import { useLoad } from "../../../shared/loading/useLoad";
import { appPaths } from "../../../shared/routing/appPaths";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import ConfirmAction from "../../../ui/overlays/ConfirmAction";
import { notifyThreadListChanged } from "../data/threadListEvents";
import { useThreadLiveClient } from "../live/threadLiveContext";
import { removalLines } from "./removalLines";

interface RemoveRunDialogProps {
  projectId: string;
  threadId: string;
  title: string;
  onClose: () => void;
}

/**
 * Removes the Run with everything it wrote (Johannes, 3 Nov 23:45; F158 part 3's erasure): what
 * goes, counted by kind, and one question; once removed, the Runs page. What keeps the Run is
 * listed, and Remove says it can't.
 */
export default function RemoveRunDialog({ projectId, threadId, title, onClose }: RemoveRunDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const live = useThreadLiveClient();
  const load = useCallback((signal: AbortSignal) => previewRunRemoval(projectId, threadId, signal), [projectId, threadId]);
  const { state, reload } = useLoad(load);
  const keptId = useId();
  const preview = state.status === "loaded" ? state.value : null;
  const lines = preview === null ? null : removalLines(preview);
  const kept = lines !== null && lines.keeps.length > 0;
  // The route's own answer: something keeps the Run, or the project no longer holds it.
  const blocked = preview !== null && !preview.removable;
  const unavailable =
    state.status === "loading" ? t("common.loading") : state.status === "failed" ? t("threads.remove.unlisted") : blocked ? t("threads.remove.keptReason") : undefined;
  return (
    <ConfirmAction
      title={t("threads.remove.title", { title })}
      confirmLabel={t("threads.actions.remove")}
      tone="dangerSolid"
      unavailable={unavailable}
      onConfirm={async () => {
        if (preview === null) return;
        await removeRun(projectId, threadId, preview.digest);
        // The followed list drops it now, whether or not the server's feed brings it (23:01 on live).
        live.threadRemoved(projectId, threadId);
        notifyThreadListChanged();
        void navigate(appPaths.threads());
      }}
      onClose={onClose}
    >
      {lines === null ? (
        <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} compact />
      ) : (
        <>
          <ul>
            {lines.goes.map((line) => <li key={line.kind}>{t(`threads.remove.goes.${line.kind}`, { count: line.count })}</li>)}
          </ul>
          {kept ? (
            <section aria-labelledby={keptId} className="space-y-1">
              <p id={keptId} className="app-label text-xs">{t("threads.remove.keptBy")}</p>
              <ul>
                {lines.keeps.map((line) => <li key={line.kind}>{t(`threads.remove.kept.${line.kind}`, { count: line.count })}</li>)}
              </ul>
            </section>
          ) : null}
        </>
      )}
    </ConfirmAction>
  );
}
